<template>
  <teleport to="body" :disabled="!isMobile">
    <div class="sidebar" :class="{ collapsed: isCollapsed }">
      <div class="sidebar-header">
        <div v-if="isMobile" class="mobile-close-btn" @click="toggleSidebar">
          <i class="bx bx-x"></i>
        </div>

        <div class="logo-container text-center" @click="goToHome">
          <transition name="fade" mode="out-in">
            <img v-if="!isCollapsed" :key="'new'" src="../assets/ctrlytics-logo-black.png" alt="Logo" class="full-logo" />
            <img v-else :key="'old'" src="../assets/ctrlytics-icon.png" alt="Logo" class="icon-logo" />
          </transition>
        </div>
      </div>

      <div class="sidebar-content">
        <div class="sidebar-top">
        <!-- Branch cockpit (expanded) — one unified navy panel: identity + stats -->
        <div class="branch-card" v-show="!isCollapsed">
          <div class="branch-card-top">
            <i class="bx bxs-store branch-icon"></i>
            <span class="branch-card-label">Your Branch</span>
          </div>
          <div class="branch-codename" :title="branchCodename">{{ branchCodename || '—' }}</div>
          <div class="branch-meta">
            <span class="branch-role-dot"></span>
            <span class="branch-role">{{ currentRole }}</span>
            <span class="branch-user" :title="currentUser" v-if="currentUser">{{ currentUser }}</span>
          </div>
          <div class="branch-divider"></div>
          <div class="branch-stats-inline">
            <div class="stat-inline">
              <span class="stat-value">{{ summaryLoading ? '–' : summary.total }}</span>
              <span class="stat-label">Devices</span>
            </div>
            <div class="stat-inline-sep"></div>
            <div class="stat-inline">
              <span class="stat-value">{{ summaryLoading ? '–' : summary.enrolled_today }}</span>
              <span class="stat-label">Today</span>
            </div>
          </div>
        </div>

        <!-- Branch cockpit (collapsed) — compact navy badge with the device count -->
        <div class="branch-mini" v-show="isCollapsed"
             :title="(branchCodename || '—') + ' · ' + (summaryLoading ? '' : summary.total + ' devices')">
          <i class="bx bxs-store"></i>
          <span class="branch-mini-count">{{ summaryLoading ? '–' : summary.total }}</span>
        </div>

        <div class="menu-section">
          <!-- Dashboard removed for POS (not needed) -->

          <!-- Top (Register User) -->
          <div v-for="item in imageItemsTop" :key="item.id" class="menu-item"
               :class="{ active: isActive(item.route) }" @click="goToPage(item.route)" :title="item.altText">
            <i :class="['bx', item.iconClass, 'icon-image']"></i>
            <span class="image-label" v-show="!isCollapsed">{{ item.altText }}</span>
          </div>

          <!-- Manage Devices -->
          <div v-for="item in imageItemsManageDevice" :key="item.id" class="menu-item"
               :class="{ active: isActive(item.route) }" @click="goToPage(item.route)" :title="item.altText">
            <i :class="['bx', item.iconClass, 'icon-image']"></i>
            <span class="image-label" v-show="!isCollapsed">{{ item.altText }}</span>
          </div>

          <!-- Mid (Manage Accounts / FAQ / Reporting) -->
          <div v-for="item in imageItemsMid" :key="item.id" class="menu-item"
               :class="{ active: isActive(item.route) }" @click="goToPage(item.route)" :title="item.altText">
            <i :class="['bx', item.iconClass, 'icon-image']"></i>
            <span class="image-label" v-show="!isCollapsed">{{ item.altText }}</span>
          </div>
        </div>
        </div>

        <div class="sidebar-footer">
          <!-- Language -->
          <div class="menu-item lang-menu-item" @click.stop="toggleLangMenu" title="Language">
            <img :src="currentLangFlag" alt="Language" class="icon-image" />
            <span class="image-label" v-show="!isCollapsed">{{ currentLangLabel }}</span>
            <span v-show="!isCollapsed" class="dropdown-arrow" :class="{ rotated: showLangMenu }">▼</span>
            <transition name="fade">
              <div v-if="showLangMenu" class="lang-dropdown">
                <div class="lang-option" @click.stop="selectLang('ENG')" title="English">
                  <img src="../assets/country_flags/en_US.svg" class="flag-mini" />
                  <span v-show="!isCollapsed">English</span>
                </div>
                <div class="lang-option" @click.stop="selectLang('BM')" title="Bahasa Malaysia">
                  <img src="../assets/country_flags/ms_MY.svg" class="flag-mini" />
                  <span v-show="!isCollapsed">Bahasa Malaysia</span>
                </div>
                <div class="lang-option" @click.stop="selectLang('th')" title="Thai">
                  <img src="../assets/country_flags/th_TH.svg" class="flag-mini" />
                  <span v-show="!isCollapsed">Thai</span>
                </div>
              </div>
            </transition>
          </div>

          <!-- Log Out -->
          <div class="menu-item logout-item" @click="showLogoutConfirmation" :title="logoutLabel">
            <i class="bx bx-log-out-circle icon-image"></i>
            <span class="image-label" v-show="!isCollapsed">{{ logoutLabel }}</span>
          </div>
        </div>
      </div>

      <!-- Logout Confirmation Modal -->
      <teleport to="body">
        <div v-if="showLogoutModal">
          <div class="overlay"></div>
          <div class="premium_modal">
            <div class="modal_content">
              <div class="modal_icon_wrapper">
                <img src="../assets/warning.png" class="modal_icon" alt="Warning" />
              </div>
              <h3 class="modal_title">{{ lang.logout_modal ? lang.logout_modal.title : "Confirm Logout" }}</h3>
              <p class="modal_message">{{ lang.logout_modal ? lang.logout_modal.message : "Are you sure you want to log out?" }}</p>
              <div class="modal_actions">
                <button class="btn_primary_large" @click="confirmLogout">{{ lang.logout_modal ? lang.logout_modal.confirm_button : "Yes, Log Out" }}</button>
                <button class="btn_secondary_large" @click="cancelLogout">{{ lang.logout_modal ? lang.logout_modal.cancel_button : "Cancel" }}</button>
              </div>
            </div>
          </div>
        </div>
      </teleport>

      <!-- Mobile Backdrop -->
      <teleport to="body">
        <transition name="fade">
          <div class="sidebar-backdrop" v-if="!isCollapsed && isMobile" @click="toggleSidebar"></div>
        </transition>
      </teleport>
    </div>
  </teleport>
</template>

<script>
import { endSession } from "../api/sessionGuard";

export default {
  emits: ["button-click"],
  data() {
    return {
      isCollapsed: JSON.parse(localStorage.getItem("sidebarCollapsed") || "false"),
      showLangMenu: false,
      imageItemsTop: [],
      imageItemsManageDevice: [],
      imageItemsMid: [],
      langmode: "ENG",
      data: [],
      data_collection: [],
      showLogoutModal: false,
      isMobile: window.innerWidth <= 768,
      branchCodename: localStorage.getItem("branch_codename") || "",
      currentRole: localStorage.role || "",
      currentUser: localStorage.getItem("username") || "",
      summary: { total: 0, enrolled_today: 0, locked: 0, active: 0 },
      summaryLoading: true,
    };
  },
  computed: {
    currentLangFlag() {
      let flag;
      if (this.langmode === "ENG") flag = require("../assets/country_flags/en_US.svg");
      else if (this.langmode === "BM") flag = require("../assets/country_flags/ms_MY.svg");
      else if (this.langmode === "th") flag = require("../assets/country_flags/th_TH.svg");
      else flag = require("../assets/country_flags/en_US.svg");
      return typeof flag === "string" ? flag : (flag.default || flag);
    },
    currentLangLabel() {
      return this.getDisplayLang(this.langmode);
    },
    dashboardLabel() {
      if (this.data && this.data[this.langmode] && this.data[this.langmode]["lower"]) {
        return this.data[this.langmode]["lower"][0];
      }
      return "Dashboard";
    },
    logoutLabel() {
      if (this.data && this.data[this.langmode] && this.data[this.langmode]["lower"]) {
        return this.data[this.langmode]["lower"][1];
      }
      return "Log Out";
    },
    lang() {
      if (this.data && this.data[this.langmode]) return this.data[this.langmode];
      return {};
    },
  },
  watch: {
    $route() {
      if (this.data && Object.keys(this.data).length > 0) {
        const savedLang = localStorage.locale || "eng";
        const langKey =
          savedLang === "eng" || savedLang === "ENG" || savedLang === "english" ? "ENG"
          : savedLang === "bm" || savedLang === "BM" || savedLang === "bahasa" ? "BM"
          : savedLang === "th" || savedLang === "TH" || savedLang === "thai" ? "th" : "ENG";
        this.langchange(langKey);
      }
    },
  },
  methods: {
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
      localStorage.setItem("sidebarCollapsed", this.isCollapsed);
      this.showLangMenu = false;
    },
    toggleLangMenu() {
      this.showLangMenu = !this.showLangMenu;
    },
    selectLang(code) {
      this.langchange(code);
      localStorage.locale = code.toLowerCase();
      this.showLangMenu = false;
    },
    isActive(route) {
      if (route === "langchange" || route === "/") return false;
      return this.$route.path === route;
    },
    getDisplayLang(code) {
      if (code === "ENG") return "ENG";
      if (code === "BM") return "BM";
      if (code === "th") return "THAI";
      return code.toUpperCase();
    },
    goToPage(route) {
      if (route === "langchange") return;
      if (route === "/") {
        localStorage.clear();
        this.$router.replace(route);
      } else {
        this.$router.replace(route);
      }
    },
    goToHome() {
      this.$router.replace("/manage-devices");
    },
    showLogoutConfirmation() {
      this.showLogoutModal = true;
    },
    confirmLogout() {
      this.showLogoutModal = false;
      // End the session server-side too. Clearing only the browser left the row live, so the
      // user's own next login evicted it and raised a security event that never happened.
      endSession().finally(() => this.goToPage("/"));
    },
    cancelLogout() {
      this.showLogoutModal = false;
    },
    langchange(val) {
      this.langmode = val;
      if (!this.data[this.langmode]) return;

      if (this.data[this.langmode]["manage"] && this.imageItemsManageDevice.length > 0) {
        const label = this.data[this.langmode]["manage"][0];
        if (this.imageItemsManageDevice[0]) this.imageItemsManageDevice[0].altText = typeof label === "string" ? label : String(label || "");
      }
      if (this.imageItemsMid && this.data[this.langmode]["mid"]) {
        this.imageItemsMid.forEach((item) => {
          let index = -1;
          if (item.id === 1) index = 0;
          else if (item.id === 2) index = 1;
          else if (item.id === 3) index = 2;
          else if (item.id === 5) index = 3;
          else if (item.id === 6) index = 4;
          if (index !== -1 && this.data[this.langmode]["mid"][index]) {
            const label = this.data[this.langmode]["mid"][index];
            item.altText = typeof label === "string" ? label : String(label || "");
          }
        });
      }
      this.$emit("button-click", this.langmode);
    },
    async onlyLoadAccepted() {
      const axios = require("axios");
      let data = JSON.stringify({ token: localStorage.token });
      let config = {
        method: "post",
        maxBodyLength: Infinity,
        // POS-scoped sidebar permissions
        url: process.env.VUE_APP_BACKEND_URL + "/v1/dashboard/sidebar-view/pos",
        headers: {
          Authorization: `Bearer ${localStorage.bearer_token}`,
          "Content-Type": "application/json",
        },
        data: data,
      };

      return axios.request(config).then((response) => {
        if (response.status === 200) {
          this.data_collection = response.data.data;
          localStorage.setItem("acceptedArrays", this.data_collection);
          var newItemsImageTop = [];
          var newimageItemsManageDevice = [];
          var newimageItemsMid = [];
          if (this.data_collection) {
            // Merged Enroll Device (replaces Register Device + Enroll User)
            if (this.data_collection.includes("Register Device")) {
              newItemsImageTop.push({ id: 1, iconClass: "bx-user-check", altText: "Register User", route: "/enroll" });
            }
            if (this.data_collection.includes("Manage Devices")) {
              newimageItemsManageDevice.push({ id: 2, iconClass: "bx-mobile", altText: "Manage Devices", route: "/manage-devices" });
            }
            // Manage Accounts removed for POS — POS accounts never manage accounts (role-fixed).
            // Legacy "Point-of-Sales Manager/Staff" accounts still carry M0000013 in their old
            // permissions map, which used to surface this tab; the endpoint now 403s it, so hide it.
            // if (this.data_collection.includes("Manage Accounts")) {
            //   newimageItemsMid.push({ id: 1, iconClass: "bx-id-card", altText: "Manage Accounts / PoS", route: "/manage-accounts" });
            // }
            newimageItemsMid.push({ id: 3, iconClass: "bx-help-circle", altText: "FAQ", route: "/faq" });
            // Reporting commented out for POS (per request)
            // if (this.data_collection.includes("Reports")) {
            //   newimageItemsMid.push({ id: 6, iconClass: "bx-briefcase-alt-2", altText: "Reporting", route: "/reporting" });
            // }
            this.imageItemsTop = newItemsImageTop;
            this.imageItemsManageDevice = newimageItemsManageDevice;
            this.imageItemsMid = newimageItemsMid;
          }
        }
      }).catch((error) => {
        console.log(error);
      });
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768;
      if (this.isMobile) this.isCollapsed = true;
    },
    fetchBranchSummary() {
      const axios = require("axios");
      axios.request({
        method: "post",
        maxBodyLength: Infinity,
        url: process.env.VUE_APP_BACKEND_URL + "/v1/dashboard/branch-summary",
        headers: {
          Authorization: `Bearer ${localStorage.bearer_token}`,
          "Content-Type": "application/json",
        },
        data: JSON.stringify({ token: localStorage.token }),
      }).then((response) => {
        if (response.status === 200 && response.data && response.data.summary) {
          this.summary = response.data.summary;
          if (response.data.branch_codename) this.branchCodename = response.data.branch_codename;
        }
        this.summaryLoading = false;
      }).catch((error) => {
        console.log(error);
        this.summaryLoading = false;
      });
    },
  },
  async mounted() {
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
    this.fetchBranchSummary();

    let data = {};
    try {
      const response = await fetch("/sidenav.json");
      data = await response.json();
    } catch (e) {
      data = {};
    }
    await this.onlyLoadAccepted().then(() => {
      if (localStorage.token == undefined) this.goToPage("/");
      this.data = data;
      const savedLang = localStorage.locale || "eng";
      const initialLang =
        savedLang === "eng" || savedLang === "ENG" || savedLang === "english" ? "ENG"
        : savedLang === "bm" || savedLang === "BM" || savedLang === "bahasa" ? "BM"
        : savedLang === "th" || savedLang === "TH" || savedLang === "thai" ? "th" : "ENG";
      this.langchange(initialLang);
    });
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkScreenSize);
  },
};
</script>

<style scoped>
.sidebar {
  height: 100dvh;
  width: 250px;
  background-color: var(--primary-color);
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  transition: width 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  overflow-x: hidden;
}
.sidebar.collapsed { width: 70px; }

.sidebar-header {
  padding: 20px 10px;
  background-color: var(--primary-color);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}
.mobile-close-btn {
  position: absolute; top: 15px; right: 15px; width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  font-size: 28px; color: var(--text-main); border-radius: 50%; transition: all 0.2s ease; z-index: 1002;
}
.mobile-close-btn:hover { background-color: rgba(0, 0, 0, 0.1); transform: scale(1.1); }

.logo-container { margin-top: 20px; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.full-logo { width: 150px; height: auto; transition: all 0.3s ease; }
.icon-logo { width: 35px; height: 35px; transition: all 0.3s ease; }

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter, .fade-leave-to { opacity: 0; }

.sidebar-content { flex: 1; display: flex; flex-direction: column; justify-content: space-between; overflow-y: auto; padding-bottom: 20px; }
.sidebar-top { display: flex; flex-direction: column; }
.menu-section { padding: 10px; display: flex; flex-direction: column; gap: 5px; }

/* Branch identity card (POS cockpit) — navy panel so it reads as info, NOT a clickable white nav item */
.branch-card {
  margin: 4px 12px 6px; padding: 14px; background-color: var(--text-main);
  border-radius: var(--border-radius-md); box-shadow: var(--shadow-md);
}
.branch-card-top { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.branch-icon { font-size: 16px; color: var(--primary-color); }
.branch-card-label {
  font-family: var(--font-family-base); font-size: 10px; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase; color: rgba(255, 255, 255, 0.6);
}
.branch-codename {
  font-family: var(--font-family-header); font-size: 16px; font-weight: 800; color: #ffffff;
  line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.branch-meta { display: flex; align-items: center; gap: 6px; margin-top: 6px; }
.branch-role-dot { width: 8px; height: 8px; border-radius: 50%; background-color: #22c55e; flex-shrink: 0; }
.branch-role { font-size: 11px; font-weight: 700; color: #ffffff; }
.branch-user {
  font-size: 11px; color: rgba(255, 255, 255, 0.6); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  border-left: 1px solid rgba(255, 255, 255, 0.2); padding-left: 6px; max-width: 110px;
}

/* Inline stats inside the branch card (no more sandwich) */
.branch-divider { height: 1px; background-color: rgba(255, 255, 255, 0.15); margin: 12px 0 10px; }
.branch-stats-inline { display: flex; align-items: center; }
.stat-inline { flex: 1; display: flex; flex-direction: column; align-items: center; text-align: center; }
.stat-inline-sep { width: 1px; align-self: stretch; background-color: rgba(255, 255, 255, 0.15); margin: 2px 4px; }
.stat-value {
  display: block; font-family: var(--font-family-header); font-size: 22px; font-weight: 800;
  color: var(--primary-color); line-height: 1.1;
}
.stat-label {
  display: block; font-size: 10px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.05em; color: rgba(255, 255, 255, 0.6); margin-top: 2px;
}

/* Collapsed cockpit badge */
.branch-mini {
  margin: 4px auto 10px; width: 46px; padding: 8px 0; background-color: var(--text-main);
  border-radius: var(--border-radius-md); box-shadow: var(--shadow-md);
  display: flex; flex-direction: column; align-items: center; gap: 2px;
}
.branch-mini i { font-size: 18px; color: var(--primary-color); }
.branch-mini-count {
  font-family: var(--font-family-header); font-size: 13px; font-weight: 800; color: #ffffff; line-height: 1;
}

.menu-item {
  display: flex; align-items: center; padding: 12px 16px; cursor: pointer;
  border-radius: var(--border-radius-md); transition: all 0.2s ease; color: var(--text-main);
  white-space: nowrap; position: relative;
}
.menu-item:hover { background-color: rgba(255, 255, 255, 0.4); }
.menu-item.active { background-color: var(--surface-color); color: var(--text-main); font-weight: 700; box-shadow: var(--shadow-sm); }

.icon-image {
  width: 20px; height: 20px; margin-right: 12px; object-fit: contain; flex-shrink: 0;
  font-size: 24px; display: flex; align-items: center; justify-content: center;
}
.sidebar.collapsed .icon-image { margin-right: 0; }
.sidebar.collapsed .menu-item { justify-content: center; padding: 12px 0; }

.image-label { font-family: var(--font-family-base); font-size: 13px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; opacity: 1; transition: opacity 0.2s ease; }

.sidebar-footer { padding: 10px; border-top: 1px solid rgba(0, 0, 0, 0.05); }

.dropdown-arrow { margin-left: auto; font-size: 10px; transition: transform 0.2s; }
.dropdown-arrow.rotated { transform: rotate(180deg); }
.lang-dropdown {
  position: absolute; bottom: 100%; left: 0; width: 100%; background-color: var(--surface-color);
  box-shadow: var(--shadow-lg); border-radius: var(--border-radius-md); padding: 5px 0; z-index: 1050; margin-bottom: 5px;
}
.sidebar.collapsed .lang-dropdown { width: 100%; left: 0; bottom: 100%; margin-bottom: 5px; }
.sidebar.collapsed .lang-menu-item { position: relative; }
.lang-option { padding: 10px 15px; display: flex; align-items: center; cursor: pointer; transition: background 0.2s; }
.sidebar.collapsed .lang-option { justify-content: center; padding: 10px 0; }
.lang-option:hover { background-color: var(--background-color); }
.flag-mini { width: 20px; height: 15px; margin-right: 10px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); }
.sidebar.collapsed .flag-mini { margin-right: 0; }

.logout-item { color: #d32f2f; }
.logout-item:hover { background-color: rgba(211, 47, 47, 0.1); }

/* Modal */
.overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.5); z-index: 9998; }
.premium_modal {
  position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 90%; max-width: 400px;
  background-color: var(--surface-color); border-radius: 20px; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 10000; padding: 30px; display: flex; flex-direction: column; align-items: center; text-align: center;
}
.modal_content { width: 100%; display: flex; flex-direction: column; align-items: center; }
.modal_icon_wrapper { margin-bottom: 10px; width: 80px; height: 80px; display: flex; align-items: center; justify-content: center; }
.modal_icon { width: 100%; height: 100%; object-fit: contain; }
.modal_title { font-family: var(--font-family-header); font-size: 24px; font-weight: 800; color: var(--text-main); margin-bottom: 10px; }
.modal_message { font-family: var(--font-family-base); font-size: 14px; color: var(--text-muted); line-height: 1.5; margin-bottom: 25px; max-width: 100%; }
.modal_actions { width: 100%; display: flex; justify-content: center; gap: 15px; }
.btn_primary_large {
  background-color: var(--primary-color); color: var(--text-main); font-family: var(--font-family-header);
  font-weight: 700; font-size: 14px; border: none; border-radius: 12px; padding: 12px 24px; min-width: 120px;
  cursor: pointer; transition: all 0.2s ease; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
.btn_primary_large:hover { transform: translateY(-2px); box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15); filter: brightness(105%); }
.btn_secondary_large {
  background-color: transparent; color: var(--text-muted); border: 1px solid #ddd; font-family: var(--font-family-header);
  font-weight: 700; font-size: 14px; border-radius: 12px; padding: 12px 24px; min-width: 120px; cursor: pointer; transition: all 0.2s ease;
}
.btn_secondary_large:hover { background-color: rgba(0, 0, 0, 0.05); border-color: #ccc; }

@media (max-width: 768px) {
  .sidebar { position: fixed; top: 0; left: 0; height: 100vh; width: 250px; z-index: 3000 !important; transform: translateX(0); }
  .sidebar.collapsed { width: 250px; transform: translateX(-100%); }
  .sidebar-backdrop { position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; background-color: rgba(0, 0, 0, 0.5); z-index: 2000 !important; }
}
</style>
